import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, Receipt, ExternalLink } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { Button, Table, Input, Dropdown } from '../ui';
import { SessionPriceTier, Session, Patient } from '../../types/firestoreTypes';
import {
  SESSION_STATUS,
  SESSION_TYPE,
  SessionStatus,
  SessionType,
} from '../../types/constants';
import { SessionList } from './SessionList';
import { SessionFilters } from './SessionFilters';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureUpgradeModal } from '../../contexts/FeatureUpgradeModalContext';
import { useToast } from '../../hooks/useToast';

interface SessionFormData {
  date: string;
  time: string;
  priceTierId: string;
  notes: string;
  isRecurring: boolean;
  frequency?: 'Weekly' | 'Every 2 Weeks' | 'Monthly';
  endDate?: string;
  sessionType: SessionType;
}

interface PatientSessionListProps {
  patientId: string;
  patientName: string;
  defaultPriceTierId: string;
  priceTiers: SessionPriceTier[];
  sessions: Session[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  isFetchingMore: boolean;
  selectedSessionIds: Set<string>;
  onSelectionChange: (sessionIds: Set<string>) => void;
  onGenerateBulkInvoice: (sessionIds: string[]) => void;
  isGeneratingBulkInvoice: boolean;
  onSessionSave: (session: Session) => Promise<void>;
  onSortChange: (column: string, direction: 'asc' | 'desc') => void;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  patient: Patient;
  therapistBookingLink?: string;
  selectedDateFilter: string;
  selectedStatusFilter: string;
  onDateFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
}

// Status Pill Component (Unchanged)
const StatusPill: React.FC<{ status: SessionStatus; label: string }> = ({
  status,
  label,
}) => {
  const statusStyles: Record<SessionStatus, string> = {
    [SESSION_STATUS.SCHEDULED]: 'bg-[#F0F9FF] text-[#026AA2]',
    [SESSION_STATUS.COMPLETED]: 'bg-[#ECFDF3] text-[#027A48]',
    [SESSION_STATUS.CANCELLED]: 'bg-[#FEF3F2] text-[#B42318]',
    [SESSION_STATUS.NO_SHOW]: 'bg-[#FFF6ED] text-[#B93815]',
    [SESSION_STATUS.INVOICED]: 'bg-[#F5F3FF] text-[#6941C6]',
  };

  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${
        statusStyles[status] || 'bg-gray-100 text-gray-600'
      }`}
    >
      {label}
    </span>
  );
};

// Main Patient Session List Component
export const PatientSessionList: React.FC<PatientSessionListProps> = ({
  patientId,
  patientName,
  defaultPriceTierId,
  priceTiers,
  sessions,
  isLoading,
  error,
  hasMore,
  onLoadMore,
  isFetchingMore,
  selectedSessionIds,
  onSelectionChange,
  onGenerateBulkInvoice,
  isGeneratingBulkInvoice,
  onSessionSave,
  onSortChange,
  sortColumn,
  sortDirection,
  patient,
  therapistBookingLink,
  selectedDateFilter,
  selectedStatusFilter,
  onDateFilterChange,
  onStatusFilterChange,
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { therapist } = useAuth();
  const { openUpgradeModal } = useFeatureUpgradeModal();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Helper function to check if a session is eligible for invoicing/selection
  const isSessionEligible = (session: Session): boolean =>
    session.status === SESSION_STATUS.COMPLETED && !session.invoiceId && Boolean(patient.billingProfileId);

  // Memoize eligible sessions for performance
  const eligibleSessions = React.useMemo(
    () => sessions.filter(isSessionEligible),
    [sessions]
  );

  // Memoize selection state for header checkbox
  const allEligibleSelected = useMemo(() => {
    return sessions.every(
      (session) =>
        session.status === SESSION_STATUS.COMPLETED &&
        !session.invoiceId &&
        selectedSessionIds.has(session.id)
    );
  }, [sessions, selectedSessionIds]);

  const someEligibleSelected = useMemo(() => {
    return sessions.some(
      (session) =>
        session.status === SESSION_STATUS.COMPLETED &&
        !session.invoiceId &&
        selectedSessionIds.has(session.id)
    );
  }, [sessions, selectedSessionIds]);

  // Row click handler for navigation
  const handleRowClick = (session: Session) => {
    trackEvent('session_viewed', {
      session_id: session.id,
      session_status: session.status,
      session_type: session.sessionType,
    });
    navigate(`/patients/${session.patientId}/sessions/${session.id}`);
  };

  // Header checkbox change handler
  const handleHeaderCheckboxChange = () => {
    const newSelectedIds = new Set(selectedSessionIds);
    if (allEligibleSelected) {
      sessions.forEach((session) => {
        if (
          session.status === SESSION_STATUS.COMPLETED &&
          !session.invoiceId
        ) {
          newSelectedIds.delete(session.id);
        }
      });
    } else {
      sessions.forEach((session) => {
        if (
          session.status === SESSION_STATUS.COMPLETED &&
          !session.invoiceId
        ) {
          newSelectedIds.add(session.id);
        }
      });
    }
    onSelectionChange(newSelectedIds);
  };

  // Row checkbox change handler
  const handleRowCheckboxChange = (session: Session) => {
    const newSelectedIds = new Set(selectedSessionIds);
    if (newSelectedIds.has(session.id)) {
      newSelectedIds.delete(session.id);
    } else {
      newSelectedIds.add(session.id);
    }
    onSelectionChange(newSelectedIds);
  };

  // Memoize filter options
  const dateFilterOptions = React.useMemo(
    () => [
      { value: 'all', label: t('filters.date.allTime') },
      { value: 'thisMonth', label: t('filters.date.thisMonth') },
      { value: 'lastMonth', label: t('filters.date.lastMonth') },
      { value: 'thisQuarter', label: t('filters.date.thisQuarter') },
      { value: 'lastQuarter', label: t('filters.date.lastQuarter') },
    ],
    [t]
  );

  const statusFilterOptions = React.useMemo(
    () => [
      { value: 'all', label: t('patientSessionList.filters.allStatuses') },
      ...Object.values(SESSION_STATUS).map((status) => ({
        value: status,
        label: t(`status.${status}`, status), // Provide fallback text
      })),
    ],
    [t]
  );

  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        key: 'select',
        header: (
          <input
            type="checkbox"
            checked={allEligibleSelected}
            onChange={handleHeaderCheckboxChange}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            aria-label={t('patientSessionList.table.selectAllAriaLabel')}
          />
        ),
        render: (session: Session) => {
          const isSelected = selectedSessionIds.has(session.id);
          const isEligible =
            session.status === SESSION_STATUS.COMPLETED && !session.invoiceId;

          return (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleRowCheckboxChange(session)}
              disabled={!isEligible}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('patientSessionList.table.selectRowAriaLabel', {
                date: session.sessionDateTime instanceof Timestamp
                  ? session.sessionDateTime.toDate().toLocaleDateString()
                  : t('patientSessionList.table.invalidDate'),
              })}
            />
          );
        },
      },
      {
        key: 'sessionDateTime',
        header: t('patientSessionList.table.headers.dateTime'),
        sortable: true,
        render: (session: Session) =>
          session.sessionDateTime instanceof Timestamp
            ? format(session.sessionDateTime.toDate(), 'Pp') // Using localized format
            : t('patientSessionList.table.invalidDate'),
      },
      {
        key: 'status',
        header: t('patientSessionList.table.headers.status'),
        sortable: false, // Status usually isn't sorted
        render: (session: Session) => (
          <StatusPill
            status={session.status}
            label={t(`status.${session.status}`, session.status)}
          />
        ),
      },
      {
        key: 'sessionType',
        header: t('patientSessionList.table.headers.type'),
        sortable: false,
        render: (s: Session) =>
          t(`sessionType.${s.sessionType}`, s.sessionType), // Provide fallback text
      },
      {
        key: 'price',
        header: t('patientSessionList.table.headers.price'),
        sortable: false, // Price might not be suitable for sorting if mixed currencies
        render: (session: Session) => {
          if (
            session.price &&
            typeof session.price.amount === 'number' &&
            typeof session.price.currency === 'string'
          ) {
            // Basic number formatting, consider using Intl.NumberFormat for better localization
            const formattedAmount = new Intl.NumberFormat(undefined, {
              // Use browser locale
              style: 'currency',
              currency: session.price.currency,
              minimumFractionDigits: session.price.currency === 'HUF' ? 0 : 2, // HUF typically has no decimals
              maximumFractionDigits: session.price.currency === 'HUF' ? 0 : 2,
            }).format(session.price.amount);
            return formattedAmount;
          }
          return t('patientSessionList.table.notApplicable');
        },
      },
      {
        key: 'durationMinutes',
        header: t('patientSessionList.table.headers.duration'),
        sortable: false,
        render: (s: Session) =>
          t('patientSessionList.table.durationValue', {
            count: s.durationMinutes,
          }),
      },
      {
        key: 'notes',
        header: t('patientSessionList.table.headers.notes'),
        sortable: false,
        render: (s: Session) => (
          <span
            title={s.notes ?? undefined}
            className="block max-w-xs truncate"
          >
            {s.notes || '-'}
          </span>
        ),
      },
    ],
    [
      t,
      allEligibleSelected,
      someEligibleSelected,
      eligibleSessions.length,
      selectedSessionIds,
      handleHeaderCheckboxChange,
      handleRowCheckboxChange,
    ]
  );

  // Add this function to handle bulk invoice generation
  const handleBulkInvoiceGeneration = () => {
    // --- Feature Gate Checks ---
    const hasBillingAccess = therapist?.packageFeaturesSnapshot?.BILLING_ACCESS_ENABLED;
    if (therapist && typeof hasBillingAccess === 'boolean' && !hasBillingAccess) {
      openUpgradeModal({
        featureNameKey: 'featureUpgradeModal.featureNames.billingAccess',
        customMessageKey: 'featureUpgradeModal.messages.billingAccessDisabled',
      });
      return;
    }

    const hasBulkInvoiceAccess = therapist?.packageFeaturesSnapshot?.INVOICE_BULK_SESSION;
    if (therapist && typeof hasBulkInvoiceAccess === 'boolean' && !hasBulkInvoiceAccess) {
      openUpgradeModal({
        featureNameKey: 'featureUpgradeModal.featureNames.bulkSessionInvoicing',
        customMessageKey: 'featureUpgradeModal.messages.bulkSessionInvoicingDisabled',
      });
      return;
    }
    // --- End Feature Gate Checks ---

    // --- Operational Prerequisite Checks ---
    // 1. Check if any sessions are selected
    if (selectedSessionIds.size === 0) {
      addToast({
        type: 'warning',
        message: t('patientSessionList.toasts.noSessionsSelected'),
      });
      return;
    }

    // 2. Check patient billing profile ID AND address completeness
    const isAddressComplete = patient?.address &&
                            patient.address.street &&
                            patient.address.city &&
                            patient.address.country &&
                            patient.address.postalCode;

    if (!patient?.billingProfileId || !isAddressComplete) {
      addToast({
        type: 'warning',
        message: t('patientSessionList.toasts.patientBillingInfoIncompleteBulk'),
      });
      return;
    }

    // 3. Verify all selected sessions are still eligible and have same currency
    const sessionsToInvoiceDetails: Session[] = [];
    let allSelectedAreTrulyEligible = true;
    let currencyMismatch = false;
    let commonCurrency: string | undefined = undefined;

    for (const sessionId of selectedSessionIds) {
      const currentSession = sessions.find(s => s.id === sessionId);
      if (!currentSession ||
          currentSession.status !== SESSION_STATUS.COMPLETED ||
          currentSession.invoiceId) {
        allSelectedAreTrulyEligible = false;
        break;
      }
      // Check for currency mismatch
      if (currentSession.price?.currency) {
        if (commonCurrency === undefined) {
          commonCurrency = currentSession.price.currency;
        } else if (commonCurrency !== currentSession.price.currency) {
          currencyMismatch = true;
          break;
        }
      } else {
        allSelectedAreTrulyEligible = false;
        console.warn(`Session ${currentSession.id} is missing price/currency information.`);
        break;
      }
      sessionsToInvoiceDetails.push(currentSession);
    }

    if (!allSelectedAreTrulyEligible) {
      addToast({
        type: 'warning',
        message: t('patientSessionList.toasts.selectedSessionsNotEligible'),
      });
      return;
    }

    if (currencyMismatch) {
      addToast({
        type: 'warning',
        message: t('patientSessionList.toasts.currencyMismatchBulk'),
      });
      return;
    }
    // --- End Operational Prerequisite Checks ---

    // If all checks pass:
    trackEvent('bulk_invoice_generated' as any, {
      session_count: sessionsToInvoiceDetails.length,
      patient_id: patientId,
    });

    // Call the callback passed from PatientDetailPage
    onGenerateBulkInvoice(sessionsToInvoiceDetails.map(s => s.id));
  };

  // Render error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
        <p className="font-medium">{t('patientSessionList.errors.title')}</p>
        <p>{error}</p>
      </div>
    );
  }

  // Main component render
  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl font-semibold text-[#101828]">
          {t('patientDetail.sessions.title')}
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              if (therapistBookingLink) {
                window.open(therapistBookingLink, '_blank', 'noopener,noreferrer');
              } else {
                addToast({
                  type: 'warning',
                  message: t('patientSessionList.toasts.bookingLinkNotConfigured'),
                });
              }
            }}
            className="whitespace-nowrap bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {t('patientSessionList.buttons.bookAppointment')}
          </Button>
          <Button
            variant="secondary"
            onClick={handleBulkInvoiceGeneration}
            disabled={isGeneratingBulkInvoice}
            title={isGeneratingBulkInvoice ? undefined : t('patientDetail.sessions.buttons.generateBulkInvoice', { count: selectedSessionIds.size })}
            className="whitespace-nowrap"
          >
            {isGeneratingBulkInvoice ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('patientDetail.sessions.buttons.generatingBulkInvoice')}
              </>
            ) : (
              <>
                <Receipt className="w-4 h-4 mr-2" />
                {t('patientDetail.sessions.buttons.generateBulkInvoice', { count: selectedSessionIds.size })}
              </>
            )}
          </Button>
        </div>
      </div>

      <SessionFilters
        filters={{
          status: selectedStatusFilter,
          dateRange: {
            start: null,
            end: null,
          },
        }}
        onFilterChange={(newFilters) => {
          onStatusFilterChange(newFilters.status);
        }}
        onSortChange={onSortChange}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
      />

      <SessionList
        sessions={sessions}
        isLoading={isLoading}
        error={error}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        isFetchingMore={isFetchingMore}
        selectedSessionIds={selectedSessionIds}
        onSelectionChange={onSelectionChange}
        onSessionSave={onSessionSave}
        priceTiers={priceTiers}
        defaultPriceTierId={defaultPriceTierId}
        patient={patient}
      />
    </div>
  );
};
